/**
 * ElderGus — the second Cat Elder (Babylon.js port).
 *
 * A distinguished tabby who teaches "The Wisdom of the Still Point"
 * — finding calm at the center of chaos — and unlocks the Chronos Bubble.
 *
 * Appears after the Apex puzzle in the Midnight Burrow.
 * Primarily visual — interaction is via the CutsceneSystem.
 *
 * Visual: cool blue/purple-tinted capsule with a bioluminescent point-light
 * glow that orbits gently, plus a subtle idle bob.
 */
import { Scene } from "@babylonjs/core/scene";
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { COLORS } from "../../config/GameConfig";
import { getAssetManager, InstantiatedModel } from "../../systems/AssetManager";

const ELDER_GUS_MODEL = "/assets/models/npcs/elder_gus.glb";
export interface ElderGusPresentation {
  seatY: number;
  shadowGenerator: ShadowGenerator;
  lampOffset: Vector3;
  readingLight?: PointLight;
  yaw?: number;
}

export class ElderGus {
  public mesh: Mesh;
  private material: StandardMaterial;
  private glowLight: PointLight;
  private readonly ownsGlowLight: boolean;
  private lightTargets: AbstractMesh[] = [];
  private baseY: number;
  private visualModel: InstantiatedModel | null = null;
  private modelMount: TransformNode | null = null;
  private casters: AbstractMesh[] = [];
  private disposed = false;

  constructor(scene: Scene, position: Vector3, private readonly presentation?: ElderGusPresentation) {
    this.baseY = position.y;

    // Body — cool blue/grey capsule (fallback until GLB loads)
    this.mesh = MeshBuilder.CreateCapsule(
      "elder_gus",
      { height: 1.5, radius: 0.38, tessellation: 16, subdivisions: 6 },
      scene,
    );
    this.mesh.position = position.clone();

    this.material = new StandardMaterial("elder_gus_mat", scene);
    this.material.diffuseColor = Color3.FromHexString('#6666aa');
    this.material.emissiveColor = Color3.FromHexString(COLORS.BIOLUMINESCENT_PURPLE).scale(0.12);
    this.material.specularColor = Color3.Black();
    this.mesh.material = this.material;
    if (presentation) {
      this.mesh.computeWorldMatrix(true);
      const min = this.mesh.getBoundingInfo().boundingBox.minimumWorld.y;
      this.mesh.bakeTransformIntoVertices(Matrix.Translation(0, presentation.seatY - min, 0));
    }

    // The local parlor has one physical lamp owner; legacy scenes keep their glow.
    this.ownsGlowLight = !presentation?.readingLight;
    this.glowLight = presentation?.readingLight ?? new PointLight("elder_gus_glow", Vector3.Zero(), scene);
    if (this.ownsGlowLight) {
      this.glowLight.diffuse = Color3.FromHexString(COLORS.BIOLUMINESCENT_BLUE);
      this.glowLight.specular = Color3.FromHexString(COLORS.BIOLUMINESCENT_PURPLE);
      this.glowLight.intensity = 2.5; this.glowLight.range = 7;
      this.glowLight.parent = this.mesh; this.glowLight.position = new Vector3(0, .5, 0);
      if (presentation) {
        this.glowLight.position.copyFrom(presentation.lampOffset);
        this.glowLight.diffuse.set(.85, .65, .36); this.glowLight.specular = Color3.Black();
        this.glowLight.intensity = .35; this.glowLight.range = 3;
      }
    }
    this.syncCasters();

    getAssetManager()
      .instantiateModel(scene, ELDER_GUS_MODEL)
      .then((model) => {
        if (this.disposed || this.mesh.isDisposed() || scene.isDisposed) {
          model.dispose();
          return;
        }
        const geometry = model.meshes.filter(m => m.getTotalVertices() > 0);
        if (presentation && !geometry.length) { model.dispose(); return; }
        if (presentation) {
          const min = Math.min(...geometry.map(m => { m.computeWorldMatrix(true); return m.getBoundingInfo().boundingBox.minimumWorld.y; }));
          this.modelMount = new TransformNode("gus_seatedArt", scene);
          this.modelMount.parent = this.mesh; this.modelMount.scaling.setAll(2.2);
          this.modelMount.rotation.y = presentation.yaw ?? 0;
          this.modelMount.position.y = presentation.seatY - this.mesh.position.y - min * 2.2;
          for (const root of model.rootNodes) root.parent = this.modelMount;

        } else {
          for (const root of model.rootNodes) {
            if (root instanceof TransformNode) {
              root.parent = this.mesh;
              root.position.copyFromFloats(0, -0.75, 0);
              root.scaling.setAll(2.2);
            }
          }
        }
        this.visualModel = model;
        for (const m of model.meshes) m.isPickable = false;
        this.mesh.isVisible = false;
        this.syncCasters();
      })
      .catch((err) => {
        console.warn("[ElderGus] Failed to load model", ELDER_GUS_MODEL, err);
      });
  }

  /** Gentle idle bob animation — call each frame. */
  update(time: number, _deltaSeconds: number): void {
    if (this.presentation) return;
    this.mesh.position.y = this.baseY + Math.sin(time * 0.8) * 0.03;

    // Orbit the glow light gently around the mesh
    const orbitRadius = 0.7;
    this.glowLight.position.x = Math.sin(time * 0.4) * orbitRadius;
    this.glowLight.position.z = Math.cos(time * 0.4) * orbitRadius;

    // Subtle intensity pulse
    this.glowLight.intensity = 2.0 + Math.sin(time * 1.2) * 0.4;
  }

  setRevealed(revealed: boolean): void {
    if (this.disposed) return;
    this.mesh.setEnabled(revealed); this.syncCasters();
  }

  private syncCasters(): void {
    if (!this.presentation) return;
    this.clearCasters();
    this.syncLightTargets();
    if (!this.mesh.isEnabled()) return;
    for (const mesh of this.visualModel?.meshes ?? [this.mesh]) if (mesh.getTotalVertices() > 0) {
      mesh.receiveShadows = true; this.presentation.shadowGenerator.addShadowCaster(mesh, false); this.casters.push(mesh);
    }
  }
  private syncLightTargets(): void {
    const otherOwners = this.glowLight.includedOnlyMeshes.filter(mesh => !this.lightTargets.includes(mesh));
    this.lightTargets = this.mesh.isEnabled()
      ? (this.visualModel?.meshes ?? [this.mesh]).filter(mesh => mesh.getTotalVertices() > 0) : [];
    this.glowLight.includedOnlyMeshes = [...otherOwners, ...this.lightTargets];
  }
  private clearCasters(): void {
    for (const mesh of this.casters) this.presentation?.shadowGenerator.removeShadowCaster(mesh, false);
    this.casters = [];
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true; this.clearCasters();
    this.visualModel?.dispose();
    this.visualModel = null;
    this.modelMount?.dispose(); this.modelMount = null;
    this.glowLight.includedOnlyMeshes = this.glowLight.includedOnlyMeshes.filter(mesh => !this.lightTargets.includes(mesh));
    this.lightTargets = [];
    if (this.ownsGlowLight) this.glowLight.dispose();
    this.material.dispose();
    this.mesh.dispose();
  }
}
